import {
  Building2,
  ExternalLink,
  Globe,
  Instagram,
  Linkedin,
  MapPin,
  Star,
  Users,
  UserX,
} from 'lucide-react-native';
import { useEffect, useRef } from 'react';
import { Animated, Easing, Linking, Platform, Pressable, StyleSheet } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { joinUniqueParts } from '../../../lib/format';
import { colors } from '../../../theme/colors';
import { withAlpha } from '../../../theme/colorUtils';
import { tokens } from '../../../theme/tokens';
import type { ResearchCompany } from '../types';
import { redactUserFacingText } from '../utils/safety';
import { CompanyContactList } from './CompanyContactList';

interface SearchResultCardProps {
  readonly company: ResearchCompany;
  readonly index: number;
}

const SOURCE_COLORS: Record<string, string> = {
  linkedin: colors.agentSource.linkedin.solid,
  browser: colors.agentSource.browser.solid,
  internal: colors.agentSource.internal.solid,
  instagram: colors.agentSource.instagram.solid,
  places: colors.status.success.main,
};

const SOURCE_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn',
  browser: 'Web',
  internal: 'FlowSellAI',
  instagram: 'Instagram',
  places: 'Maps',
  ai_enriched: 'AI',
};

const HEADLINE_LINKEDIN_LABEL = 'LinkedIn (from a profile headline)';

function sourceLabel(source: string, employerFromHeadline: boolean | undefined): string {
  if (source === 'linkedin' && employerFromHeadline) return HEADLINE_LINKEDIN_LABEL;
  return SOURCE_LABELS[source] ?? source;
}

function formatRating(company: ResearchCompany): string | undefined {
  if (company.rating === undefined || company.rating === null) return undefined;
  const reviews = company.reviewCount
    ? ` (${company.reviewCount.toLocaleString('en-US')} reviews)`
    : '';
  return `${company.rating.toFixed(1)}${reviews}`;
}

export function SearchResultCard({ company, index }: SearchResultCardProps) {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(16)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: 300,
        delay: index * 40,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: 300,
        delay: index * 40,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [opacity, translateY, index]);

  function handleOpenUrl(url: string) {
    if (Platform.OS === 'web') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(url);
    }
  }

  const sources = company.sources?.length ? company.sources : [company.source];
  const sourceLabels = sources.map((source) => sourceLabel(source, company.employerFromHeadline));
  const ratingLabel = formatRating(company);
  const rankLabel = `Ranking #${index + 1}`;
  const scoreLabel = formatScore(company.confidenceScore);

  return (
    <Animated.View style={[styles.card, { opacity, transform: [{ translateY }] }]}>
      <Box flexDirection="row" alignItems="center" justifyContent="space-between" gap="sm">
        <Box flexDirection="row" alignItems="center" gap="xs" flex={1}>
          <Building2 size={16} color={colors.text.secondary} strokeWidth={1.8} />
          <Text
            variant="bodySmall"
            color={colors.text.primary}
            style={styles.companyName}
            numberOfLines={1}
          >
            {redactUserFacingText(company.name)}
          </Text>
        </Box>
      </Box>

      <Box
        flexDirection="row"
        flexWrap="wrap"
        gap="xxs"
        accessible
        accessibilityLabel={`Found on ${sourceLabels.join(', ')}`}
      >
        {sources.map((source, position) => {
          const sourceColor = SOURCE_COLORS[source] ?? colors.text.tertiary;
          return (
            <Box
              key={source}
              style={[styles.sourceBadge, { backgroundColor: withAlpha(sourceColor, 0.13) }]}
            >
              <Box style={[styles.sourceDot, { backgroundColor: sourceColor }]} />
              <Text variant="caption" color={sourceColor} style={styles.sourceText}>
                {sourceLabels[position]}
              </Text>
            </Box>
          );
        })}
      </Box>

      <Box style={styles.rankingRow}>
        <Text variant="caption" color={colors.primary.neon} style={styles.rankingText}>
          {rankLabel}
        </Text>
        {scoreLabel && (
          <Text variant="caption" color={colors.text.secondary} style={styles.scoreText}>
            {scoreLabel}
          </Text>
        )}
      </Box>

      {company.industry && (
        <Text variant="caption" color={colors.text.tertiary} numberOfLines={1}>
          {redactUserFacingText(company.industry)}
          {company.subIndustry ? ` / ${redactUserFacingText(company.subIndustry)}` : ''}
        </Text>
      )}

      {company.description && (
        <Text
          variant="caption"
          color={colors.text.secondary}
          numberOfLines={2}
          style={styles.description}
        >
          {redactUserFacingText(company.description)}
        </Text>
      )}

      <Box flexDirection="row" flexWrap="wrap" gap="md" marginTop="xxs">
        {(company.employeeCount || company.employeeRange) && (
          <Box flexDirection="row" alignItems="center" gap="xxs">
            <Users size={12} color={colors.text.tertiary} strokeWidth={1.8} />
            <Text variant="caption" color={colors.text.tertiary}>
              {company.employeeCount
                ? `${company.employeeCount.toLocaleString()}`
                : company.employeeRange}
            </Text>
          </Box>
        )}

        {ratingLabel && (
          <Box flexDirection="row" alignItems="center" gap="xxs">
            <Star size={12} color={colors.status.warning.main} strokeWidth={1.8} />
            <Text variant="caption" color={colors.text.secondary}>
              {ratingLabel}
            </Text>
          </Box>
        )}

        {(company.location || company.city || company.country) && (
          <Box flexDirection="row" alignItems="center" gap="xxs">
            <MapPin size={12} color={colors.text.tertiary} strokeWidth={1.8} />
            <Text variant="caption" color={colors.text.tertiary} numberOfLines={1}>
              {redactUserFacingText(
                joinUniqueParts([company.city, company.country]) || company.location,
              )}
            </Text>
          </Box>
        )}
      </Box>

      <Box flexDirection="row" flexWrap="wrap" gap="sm" marginTop="xs">
        {company.website ? (
          <Pressable
            style={styles.linkButton}
            onPress={() => handleOpenUrl(company.website!)}
            accessibilityRole="link"
            accessibilityLabel={`Visit ${company.name} website`}
          >
            <Globe size={12} color={colors.text.interactive} strokeWidth={1.8} />
            <Text variant="caption" color={colors.text.interactive}>
              Website
            </Text>
            <ExternalLink size={10} color={colors.text.interactive} strokeWidth={1.8} />
          </Pressable>
        ) : null}

        {company.linkedinUrl ? (
          <Pressable
            style={styles.linkButton}
            onPress={() => handleOpenUrl(company.linkedinUrl!)}
            accessibilityRole="link"
            accessibilityLabel={`View ${company.name} on LinkedIn`}
          >
            <Linkedin size={12} color={colors.agentSource.linkedin.solid} strokeWidth={1.8} />
            <Text variant="caption" color={colors.agentSource.linkedin.solid}>
              LinkedIn
            </Text>
          </Pressable>
        ) : null}

        {company.instagramUrl ? (
          <Pressable
            style={styles.linkButton}
            onPress={() => handleOpenUrl(company.instagramUrl!)}
            accessibilityRole="link"
            accessibilityLabel={`View ${company.name} on Instagram`}
          >
            <Instagram size={12} color={colors.agentSource.instagram.solid} strokeWidth={1.8} />
            <Text variant="caption" color={colors.agentSource.instagram.solid}>
              Instagram
            </Text>
          </Pressable>
        ) : null}
      </Box>

      {company.contactStatus === 'no_contact' ? (
        <Box
          flexDirection="row"
          alignItems="center"
          gap="xxs"
          marginTop="xs"
          accessible
          accessibilityLabel={`${company.name} has no contact information`}
        >
          <UserX size={12} color={colors.text.tertiary} strokeWidth={1.8} />
          <Text variant="caption" color={colors.text.tertiary}>
            No contact
          </Text>
        </Box>
      ) : null}

      <CompanyContactList contacts={company.contacts ?? []} companyName={company.name} />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    borderRadius: tokens.radius.lg,
    backgroundColor: colors.background.card,
    padding: 12,
    gap: 6,
  },
  companyName: {
    fontWeight: '600',
    flex: 1,
  },
  sourceBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
  },
  sourceDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
  },
  sourceText: {
    fontSize: 10,
    fontWeight: '500',
  },
  rankingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rankingText: {
    fontWeight: '600',
  },
  scoreText: {
    fontWeight: '500',
  },
  description: {
    lineHeight: 16,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
});

function formatScore(score: string | number | undefined): string | undefined {
  if (score === undefined || score === null || score === '') return undefined;
  const numeric = typeof score === 'number' ? score : Number(score);
  if (!Number.isFinite(numeric)) return undefined;
  const percent = numeric <= 1 ? Math.round(numeric * 100) : Math.round(numeric);
  return `Score ${percent}%`;
}
